import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, delay, finalize, forkJoin, map, Observable, of, startWith, tap } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  TabellaCfgDinamicaComponent,
  TabellaDinamica,
} from 'src/app/components/tabella-cfg-dinamica/tabella-cfg-dinamica.component';
import {
  AziendaService,
  FormaGiuridicaGroup,
  InfoFormVsme,
} from 'src/app/services/azienda/azienda.service';
import { JsonFileService, Nace, Paese } from 'src/app/services/json-file/json-file.service';

@Component({
  selector: 'app-info-form-vsme',
  standalone: false,

  templateUrl: './info-form-vsme.component.html',
  styleUrl: './info-form-vsme.component.scss',
})
export class InfoFormVsmeComponent implements OnInit, AfterViewInit {
  @ViewChild('cfgTabellaDinamica') tabellaSitiCmp!: TabellaCfgDinamicaComponent;
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  private _infoFormVsmeInput: InfoFormVsme | undefined;

  @Input()
  set infoFormVsme(value: InfoFormVsme | undefined) {
    this._infoFormVsmeInput = value;
  }

  get infoFormVsme(): InfoFormVsme | undefined {
    return this._infoFormVsmeInput;
  }

  public _disableForm: boolean = false;
  @Input()
  set disableForm(disableForm: boolean) {
    this._disableForm = disableForm;
  }
  get disableForm() {
    return this._disableForm;
  }

  private _idAzienda: string | undefined;
  @Input()
  set idAzienda(value: string | undefined) {
    this._idAzienda = value;
  }

  get idAzienda(): string | undefined {
    return this._idAzienda;
  }

  private _isModulo: boolean = false;
  @Input()
  set isModulo(value: boolean) {
    this._isModulo = value;
  }

  get isModulo(): boolean {
    return this._isModulo;
  }

  public tabellaSiti: TabellaDinamica | undefined;
  private nacePreSelect: Nace | undefined = undefined;
  private paesePreSelect: Paese | undefined = undefined;
  private codiciNaceArray: Nace[] | undefined = undefined;
  private codiciPaeseArray: Paese[] | undefined = undefined;
  public nace: Nace[] = [];
  public paese: Paese[] = [];
  public showAltriRecordDisponibili: boolean = false;
  public filteredNace: Observable<Nace[]> | undefined;
  public filteredPaese: Observable<Paese[]> | undefined;

  /* Form Controls */
  public formInfoVsme = new FormGroup({
    statoPatrimoniale: new FormControl<number | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.pattern('^[0-9]*$'), Validators.min(0)],
    }),
    /* codice Nace */
    codiciNace: new FormControl<string | Nace>('', {
      nonNullable: true,
      validators: [],
    }),
    paeseOperazioni: new FormControl<string | Paese>('', {
      nonNullable: true,
      validators: [],
    }),

    formaGiuridica: new FormControl<string[]>([], {
      nonNullable: true,
      validators: [],
    }),
    tipoAzienda: new FormControl<'MICROIMPRESA' | 'PMI' | undefined>(undefined, {
      nonNullable: true,
      validators: [],
    }),
  });

  public formaGiuridicaGroups: FormaGiuridicaGroup[] = [
    {
      titolo: 'Imprese individuali',
      disabled: false,
      formaGiuridica: [
        { id: 'ditta-individuale-0', valore: 'Ditta individuale', disabled: false },
        { id: 'impresa-familiare-1', valore: 'Impresa familiare', disabled: false },
      ],
    },
    {
      titolo: 'Società di persone',
      disabled: false,
      formaGiuridica: [
        { id: 'societa-semplice-0', valore: 'S.s. - Società semplice', disabled: false },
        {
          id: 'societa-nome-collettivo-1',
          valore: 'S.n.c. - Società in nome collettivo',
          disabled: false,
        },
        {
          id: 'societa-accomandita-semplice-2',
          valore: 'S.a.s. - Società in accomandita semplice',
          disabled: false,
        },
      ],
    },
    {
      titolo: 'Società di capitali',
      disabled: false,
      formaGiuridica: [
        { id: 'srl-0', valore: 'S.r.l. - Società a responsabilità limitata', disabled: false },
        {
          id: 'srls-1',
          valore: 'S.r.l.s. - Società a responsabilità limitata semplificata',
          disabled: false,
        },
        { id: 'spa-2', valore: 'S.p.A. - Società per azioni', disabled: false },
        { id: 'sapa-3', valore: 'S.a.p.a. - Società in accomandita per azioni', disabled: false },
      ],
    },
    {
      titolo: 'Forme speciali',
      disabled: false,
      formaGiuridica: [
        { id: 'sb-0', valore: 'S.b. - Società Benefit', disabled: false },
        { id: 'cooperativa-1', valore: 'Cooperativa', disabled: false },
        { id: 'cooperativa-sociale-2', valore: 'Cooperativa sociale', disabled: false },
        { id: 'consorzio-3', valore: 'Consorzio', disabled: false },
        { id: 'geie-4', valore: 'GEIE - Gruppo Europeo di Interesse Economico', disabled: false },
        {
          id: 'startup-innovativa-5',
          valore: 'Start-up innovativa (status, non forma giuridica)',
          disabled: false,
        },
        { id: 'ati-6', valore: 'ATI - Associazione Temporanea di Imprese', disabled: false },
      ],
    },
  ];

  constructor(
    public aziendaService: AziendaService,
    private readonly jsonFileService: JsonFileService,
  ) {}

  ngOnInit(): void {
    this.formInfoVsme
      .get('formaGiuridica')
      ?.valueChanges.pipe(delay(0)) // <--- Add this
      .subscribe((formaGiuridicaSelezionata) => {
        this.aggiornaGroupDisabling(formaGiuridicaSelezionata);
      });
    /* Construzione Tabella siti */
    if (this.infoFormVsme?.tabellaSiti) {
      this.tabellaSiti = this.infoFormVsme?.tabellaSiti;
    } else {
      this.tabellaSiti = this._creaColonneTabella();
    }
  }

  ngAfterViewInit(): void {
    this.spinnerOver.show();
    this.valoriIniziali()
      .pipe(
        finalize(() => {
          if (this.disableForm) {
            this.formInfoVsme.disable();
          }
        }),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.naceAutoComplete();
            this.paeseAutoComplete();
          }
          this.spinnerOver.hide();
        },
        error: (err) => {
          console.error('Errore nella subscribe:', err);
          this.spinnerOver.hide();
        },
      });
  }

  public valoriIniziali(): Observable<boolean> {
    /* valori iniziali in modifica per autocomplete */
    let naceIniziale: string = '';
    let paeseIniziale: string = '';

    if (this._infoFormVsmeInput) {
      Object.keys(this._infoFormVsmeInput).forEach((value, index, array) => {
        if (value === 'codiciNace') {
          const record = this._infoFormVsmeInput as any;
          const valoreNace = record?.codiciNace?.length ? record?.codiciNace[0] : '';
          naceIniziale = valoreNace;
        } else if (value === 'paeseOperazioni') {
          const record = this._infoFormVsmeInput as any;
          const valorePaese = record?.paeseOperazioni?.length ? record?.paeseOperazioni[0] : '';
          paeseIniziale = valorePaese;
        } else {
          this.formInfoVsme.get(value)?.setValue((this._infoFormVsmeInput as any)[value]);
        }
      });

      const obsPerForkJoin: {
        nace: Observable<Nace[]>;
        paese: Observable<Paese[]>;
      } = {
        nace: of([]),
        paese: of([]),
      };

      if (naceIniziale) {
        obsPerForkJoin.nace = this.jsonFileService.getNaceById([naceIniziale]);
      }

      if (paeseIniziale) {
        obsPerForkJoin.paese = this.jsonFileService.getPaeseById([paeseIniziale]);
      }

      return forkJoin(obsPerForkJoin).pipe(
        map((result) => {
          this.nacePreSelect = result.nace[0] || undefined;
          this.paesePreSelect = result.paese[0] || undefined;
          return true;
        }),
        catchError((err) => {
          console.error(err);
          return of(false);
        }),
      );
    } else {
      return of(true);
    }
  }

  onKeyDownDip(event: KeyboardEvent): void {
    if (event.key === ',' || event.key === '.') {
      // Impedisce l'inserimento del carattere "-"
      event.preventDefault();
    }
  }

  aggiornaGroupDisabling(formaGiuridicaSelezionata: string[] | null) {
    const selectedId = formaGiuridicaSelezionata?.[0] ?? null;

    for (const group of this.formaGiuridicaGroups) {
      group.formaGiuridica.forEach((forma) => {
        forma.disabled = selectedId ? forma.id !== selectedId : false;
      });
    }
  }

  removeFormaGiuridica(idFormaGiuridica: string) {
    const formaGiuridicaControl = this.formInfoVsme.get('formaGiuridica');

    const selected = formaGiuridicaControl?.value || [];
    const updated = selected.filter((idForma) => idForma !== idFormaGiuridica);
    formaGiuridicaControl?.setValue(updated);
  }

  getFormaGiuridicaViewValue(idFormaGiuridica: string): string {
    for (const group of this.formaGiuridicaGroups) {
      const match = group.formaGiuridica.find((forma) => forma.id === idFormaGiuridica);
      if (match) return match.valore;
    }
    return idFormaGiuridica;
  }

  readonlyCodiceNace() {
    const codNace = this.formInfoVsme.get('codiciNace')?.getRawValue();
    if (codNace._id) {
      return true;
    }
    if (codNace.section) {
      return true;
    }

    return false;
  }

  readonlyCodicePaese() {
    const paese = this.formInfoVsme.get('paeseOperazioni')?.getRawValue();
    if (paese.countryCode) {
      return true;
    }

    return false;
  }

  /* displayfn e filter per codice nace */
  displayFnNace(nace: Nace): string {
    return nace && nace._id ? nace._id + ' - ' + nace.desc : '';
  }

  /* displayfn e filter per codice paese */
  displayFnPaese(paese: Paese): string {
    return paese && paese.countryCode ? paese.countryCode + ' - ' + paese.countryName : '';
  }

  private _filterNace(name: string, options: Nace[]): Nace[] {
    const filterValue = name.toLowerCase();
    return this.nace.filter(
      (option) =>
        option.desc.toLowerCase().includes(filterValue) ||
        option._id.toLowerCase().includes(filterValue),
    );
  }

  private _filterPaese(name: string, options: Paese[]): Paese[] {
    const filterValue = name.toLowerCase();
    return this.paese.filter(
      (option) =>
        option.countryName.toLowerCase().includes(filterValue) ||
        option.countryCode.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Metodo che mi riempie l'autocompelte dei codici nace
   */
  public naceAutoComplete() {
    this.codiciNaceArray = this.aziendaService.getCodiciNace();
    if (this.codiciNaceArray) {
      this.nace = this.codiciNaceArray;

      const naceControl = this.formInfoVsme.get('codiciNace');
      if (naceControl) {
        this.filteredNace = naceControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            const name: string = typeof value === 'string' ? value : value?.desc;
            return name ? this._filterNace(name, this.nace) : this.nace.slice();
          }),
          tap((esito: any) => {
            if (esito.length > 10) {
              this.showAltriRecordDisponibili = true;
            } else {
              this.showAltriRecordDisponibili = false;
            }
          }),
        );
        if (this.nacePreSelect) {
          naceControl.setValue(this.nacePreSelect);
          this.nacePreSelect = undefined;
        }
      }
    }
  }

  /**
   * Metodo che mi riempie l'autocompelte dei paesi
   */
  public paeseAutoComplete() {
    this.codiciPaeseArray = this.aziendaService.getCodiciPaese();
    if (this.codiciPaeseArray) {
      this.paese = this.codiciPaeseArray;

      const paeseControl = this.formInfoVsme.get('paeseOperazioni');
      if (paeseControl) {
        this.filteredPaese = paeseControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            const name: string = typeof value === 'string' ? value : value?.countryName;
            return name ? this._filterPaese(name, this.paese) : this.paese.slice();
          }),
          tap((esito: any) => {
            if (esito.length > 10) {
              this.showAltriRecordDisponibili = true;
            } else {
              this.showAltriRecordDisponibili = false;
            }
          }),
        );
        if (this.paesePreSelect) {
          paeseControl.setValue(this.paesePreSelect);
          this.paesePreSelect = undefined;
        }
      }
    }
  }

  clearInput(formControlName: string, event?: Event): void {
    const field = this.formInfoVsme.get(formControlName);

    if (!field?.disabled) {
      field?.reset();
      field?.markAsDirty();
      field?.markAsTouched();
    }
    if (event) event.stopPropagation();
  }

  /**
   * Creo colonne pre configurate se non esiste una tabella creata nell'azienda.
   * Tabella per gestione SITI
   * @returns
   */
  private _creaColonneTabella() {
    const tabella: TabellaDinamica = {
      id: undefined,
      titoloTabella: '',
      colonne: [
        {
          id: 'c1',
          label: 'Siti',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
        {
          id: 'c2',
          label: 'Indirizzo',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
        {
          id: 'c3',
          label: 'Codice postale',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
        {
          id: 'c4',
          label: 'Città',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
        {
          id: 'c5',
          label: 'Paese',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
        {
          id: 'c6',
          label: 'Coordinate\n(geolocalizzazione)',
          tipoColonna: 'TESTO',
          idCatUm: undefined,
          unitaDiMisura: undefined,
        },
      ],
      righe: [],
      totaleRighe: false,
      totaleColonne: false,
    };
    return tabella;
  }
}
